import { z } from "zod";

// ---------------------------------------------------------------------------
// Billing, practice side: test-kit supplies (docs/specs/2026-10-25-billing-plumbing-design.md, ADR 0008)
// `GET /billing/supplies` and `PUT /billing/supplies/:locationId`, organization-scoped through X-Organization-Id,
// `clinical:read` to read and `clinical:manage` to write, the membership's location scope respected. Counts only:
// nothing here names a patient or an encounter.
// ---------------------------------------------------------------------------

/** A calendar date, `YYYY-MM-DD`, in the location's own timezone. */
const CalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** What the practice saved for a location: the opening count and when it was taken, the reorder threshold, and whether to reorder automatically. */
export const SupplySettings = z.object({
  openingKits: z.number().int().min(0),
  openingAsOf: CalendarDate,
  reorderThreshold: z.number().int().min(0),
  autoReorder: z.boolean(),
  /** Optimistic-concurrency version; 1 after the first save. */
  version: z.number().int().min(1),
});
export type SupplySettings = z.infer<typeof SupplySettings>;

/** One location's supplies: the derived test-kit balance (opening + 20 per shipped tray package - executed tests since the opening date), null until settings exist. */
export const LocationSupplies = z.object({
  locationId: z.string().uuid(),
  name: z.string(),
  balance: z.number().int().nullable(),
  settings: SupplySettings.nullable(),
  /** True when a balance exists and is at or below the reorder threshold. */
  lowStock: z.boolean(),
});
export type LocationSupplies = z.infer<typeof LocationSupplies>;

/** One row per active location the caller may see. */
export const SuppliesResponse = z.object({ locations: z.array(LocationSupplies) });
export type SuppliesResponse = z.infer<typeof SuppliesResponse>;

/** Creates (version 0) or replaces the location's settings; `409 version_conflict` when the version is stale. */
export const UpdateSupplySettingsRequest = z
  .object({
    version: z.number().int().min(0),
    openingKits: z.number().int().min(0),
    openingAsOf: CalendarDate,
    reorderThreshold: z.number().int().min(0),
    autoReorder: z.boolean(),
  })
  .strict();
export type UpdateSupplySettingsRequest = z.infer<typeof UpdateSupplySettingsRequest>;
